import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="New Password"
      subtitle="Choose a strong password"
      footer={
        <Link to="/login" className="text-primary font-medium hover:underline" data-testid="back-to-login-link">
          Back to sign in
        </Link>
      }
    >
      {!token ? (
        <p className="text-sm text-brand-red" data-testid="reset-no-token">
          Invalid reset link. Please request a new one.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters" required data-testid="reset-password-input" />
          </div>
          {error && <p className="text-sm text-brand-red" data-testid="reset-error">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Reset Password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
